import './App.css';
import SearchField from './SearchField';
import ResultCardList from './ResultCardList';
import PlaceholderSearch from './PlaceholderSearch';
import Footer from './Footer';
import Pagination from './Pagination';
import { createJsonPostHeaders } from './httpUtils';

import React, { useState, useEffect } from 'react';
import { useAuth } from "react-oidc-context";
import { handleCognitoResponse } from './authService.ts';
import Sidebar from './Sidebar';

function isAuthLoading(auth) {
  return auth.activeNavigator === "signinRedirect" || auth.isLoading;
}

// function extractSuggestionItems(responseJson) {
//   if (!responseJson || typeof responseJson !== 'object') {
//     throw new Error('Invalid suggestions response format');
//   }

//   if (!Array.isArray(responseJson.suggestions)) {
//     throw new Error('Invalid suggestions response format');
//   }

//   return responseJson.suggestions
//     .map((item) => (typeof item === 'string' ? item.trim() : ''))
//     .filter(Boolean);
// }

function App() {
  const [input, setInput] = useState('');
  const [data, setData] = useState([]);
  const [query, setQuery] = useState('');
  const [originalQuery, setOriginalQuery] = useState('');
  const [spellingCorrectionEnabled, setSpellingCorrectionEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const standardSelectedFields = ["broadcast_series_title", "title", "teaser_text", "keywords"];
  const [selectedFields, setSelectedFields] = useState(standardSelectedFields);
  const [fieldBoosts, setFieldBoosts] = useState({ title: 2, keywords: 1.5 });
  const boostValues = selectedFields.map((field) => fieldBoosts[field] ?? 1);
  const [docTypeBoosts, setDocTypeBoosts] = useState([
    { value: 'channel', boost: 1.01 },
    { value: 'subchannel', boost: 1.01 },
    { value: 'broadcastSeries', boost: 1.035 },
  ]);
  const activeDocTypeBoosts = docTypeBoosts.filter(
    (item) => item.value.trim() && item.boost !== '' && !Number.isNaN(Number(item.boost))
  );
  const docTypeBoostingRules = activeDocTypeBoosts.map((item) => ({
    field: 'document_type',
    value: item.value.trim(),
    weight: Number(item.boost),
  }));
  const [filters, setFilters] = useState([]);
  const [notFilters, setNotFilters] = useState([]);

  const [localWeight] = useState(0.3);

  const [intentSuggestions, setIntentSuggestions] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);



  const auth = useAuth();
  useEffect(() => {
    if (isAuthLoading(auth)) {
      // Wait for the redirect to complete
      return;
    }
    if (!isAuthLoading(auth)) {
      if (auth.isAuthenticated) {
        setIsModalOpen(false);
      } else {
        // Handle the Cognito response after redirect
        handleCognitoResponse()
          .then((isAuthenticated) => {
            if (isAuthenticated) {
              setIsModalOpen(false);
            } else {
              setIsModalOpen(true);
            }
          })
          .catch((err) => {
            console.error("Error handling Cognito response:", err);
            setIsModalOpen(true);
          });
      }
    }
  }, [auth, auth.isLoading, auth.isAuthenticated, auth.activeNavigator]);

  const handleLogin = () => {
    const CLIENT_ID = config.CLIENT_ID;
    const COGNITO_DOMAIN = config.COGNITO_DOMAIN;
    const redirectUri = `${window.location.origin}`;

    const loginUrl = `${COGNITO_DOMAIN}/login?client_id=${CLIENT_ID}&response_type=code&scope=email+openid+phone&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = loginUrl;
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sliderValue, setSliderValue] = useState(7000);
  const [maxResults, setMaxResults] = useState(10);
  const [fromPos, setFromPos] = useState(0);
  // Tracks whether the current result set came from the A-Z letter search (PlaceholderSearch),
  // so pagination and reset can distinguish it from a regular hybrid/simple text search.
  const [lastSearchedLetter, setLastSearchedLetter] = useState(null);

  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetch('/config.json')
      .then((res) => {
        if (!res.ok) {
          throw new Error("config.json not found or not accessible");
        }
        return res.json();
      })
      .then((json) => {
        if (!json.AUGMENT_ENDPOINT ||
          !json.DOCUMENTS_ENDPOINT ||
          !json.CLIENT_ID ||
          !json.COGNITO_DOMAIN ||
          !json.HTTP_ACCEPT ||
          !json.SUGGESTION_ENDPOINT ||
          !json.PREFIX_SEARCH_ENDPOINT) {
          throw new Error("Missing required config values in config.json");
        }
        setConfig(json);
      })
      .catch((err) => {
        console.error('❌ Fehler beim Laden der Konfiguration:', err);
        setError("Die Anwendung konnte nicht gestartet werden – Konfiguration fehlt oder ist ungültig.");
      });
  }, []);

  const SEARCH_ENDPOINT = config?.AUGMENT_ENDPOINT;
  const DOCUMENTS_ENDPOINT = config?.DOCUMENTS_ENDPOINT;
  const SEARCH_SUGGESTIONS_ENDPOINT = config?.SUGGESTION_ENDPOINT;
  const PREFIX_SEARCH_ENDPOINT = config?.PREFIX_SEARCH_ENDPOINT;

  useEffect(() => {
    // SUGGESTION_ENDPOINT is not ready on the backend yet - keep autocomplete
    // disabled so it doesn't constantly error. Re-enable this block once it's live:
    //
    // if (!config) {
    //   return;
    // }
    //
    // const trimmedInput = input.trim();
    //
    // if (trimmedInput.length < 3) {
    //   setSearchSuggestions([]);
    //   setSuggestionsLoading(false);
    //   return;
    // }
    //
    // const controller = new AbortController();
    //
    // (async () => {
    //   try {
    //     setSuggestionsLoading(true);
    //
    //     const response = await fetch(SEARCH_SUGGESTIONS_ENDPOINT, {
    //       method: 'POST',
    //       headers: createJsonPostHeaders(config),
    //       body: JSON.stringify({
    //         query: trimmedInput,
    //         relevant_fields: selectedFields,
    //         num_of_suggestions: 5,
    //       }),
    //       signal: controller.signal,
    //     });
    //
    //     if (!response.ok) {
    //       throw new Error(`Suggestions failed with status ${response.status}`);
    //     }
    //
    //     const responseJson = await response.json();
    //     const normalizedSuggestions = extractSuggestionItems(responseJson)
    //       .filter((suggestion) => suggestion.toLowerCase() !== trimmedInput.toLowerCase())
    //       .slice(0, 5);
    //
    //     setSearchSuggestions(normalizedSuggestions);
    //   } catch (err) {
    //     if (err.name !== 'AbortError') {
    //       console.error('Failed to fetch suggestions:', err);
    //       setSearchSuggestions([]);
    //     }
    //   } finally {
    //     if (!controller.signal.aborted) {
    //       setSuggestionsLoading(false);
    //     }
    //   }
    // })();
    //
    // return () => {
    //   controller.abort();
    // };
    setSearchSuggestions([]);
    setSuggestionsLoading(false);
  }, [config, input, selectedFields, SEARCH_SUGGESTIONS_ENDPOINT]);

  if (error) {
    return (
      <div className="fatal-error">
        <h2>❌ Fehler beim Laden der Anwendung</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!config) return <p>Lade Konfiguration...</p>;


  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSliderChange = (event) => {
    const value = event.target.value;
    setSliderValue(value);
  };

  const handleMaxResultsChange = (event) => {
    setMaxResults(Number(event.target.value));
  };

  const handleReset = () => {
    setData([]);
    setOriginalQuery('');
    setQuery('');
    setInput('');
    setError(null);
    setFromPos(0);
    setLastSearchedLetter(null);
    setIntentSuggestions([]);
    setSearchSuggestions([]);
  };

  const buildSearchPayload = (query, fromPosition, disableSpellingCorrection = false) => {
    const searchFields = selectedFields.map((field, index) => ({
      name: field,
      boost: boostValues[index] || 1
    }));

    const searchTerm = { text: query, target: 'hybrid' };
    if (disableSpellingCorrection) {
      searchTerm.query_correction = { spelling_correction_enabled: 'false' };
    }

    const payload = {
      search_terms: [searchTerm],
      minimum_score: sliderValue * 0.0001,
      keyword_search_config: {
        search_fields: searchFields
      },
      maximum_results: maxResults,
      from_position: fromPosition
    };

    if (docTypeBoostingRules.length > 0) {
      payload.ranking_config = {
        boosting_rules: {
          value_boosting_rules: docTypeBoostingRules
        }
      };
    }

    const mustFilters = filters
      .filter(f => f.key && f.value)
      .map(f => ({ term: { [f.key]: f.value } }));

    const mustNotFilters = notFilters
      .filter(f => f.key && f.value)
      .map(f => ({ term: { [f.key]: f.value } }));

    if (mustFilters.length > 0 || mustNotFilters.length > 0) {
      payload.advanced_filter = {
        filter_expression: {
          bool: {
            ...(mustFilters.length > 0 && { must: mustFilters }),
            ...(mustNotFilters.length > 0 && { must_not: mustNotFilters })
          }
        }
      };
    }

    return payload;
  };

  // To-Do: We use the KEYWORD_SEARCH Endpoint here is a minimum score relevant? - The reco team uses it in the sample
  const PREFIX_SEARCH_MINIMUM_SCORE = 0.2;

  const buildPrefixSearchPayload = (letter, fromPosition = 0) => ({
    search_term: {
      text: letter.toLowerCase(),
      query_mode: 'prefix'
    },
    minimum_score: PREFIX_SEARCH_MINIMUM_SCORE,
    search_config: {
      search_fields: [
        { name: 'broadcast_series_title.keyword' },
        { name: 'title.keyword' }
      ]
    },
    maximum_results: maxResults,
    from_position: fromPosition,
    advanced_filter: {
      filter_expression: {
        bool: {
          must: [
            { term: { site: 'kika' } },
            { term: { platforms: 'kikade' } },
            { terms: { document_type: ['channel', 'broadcastSeries'] } }
          ]
        }
      }
    },
    sort_by: {
      field: 'title.keyword',
      direction: 'asc'
    }
  });

  const handleSearchWithCorrection = async (fromPosition = fromPos) => {
    setSpellingCorrectionEnabled(true);
    setLastSearchedLetter(null);
    handleSearch({
      endpoint: SEARCH_ENDPOINT,
      body: JSON.stringify(buildSearchPayload(input, fromPosition, false)),
      searchQuery: input
    });
  };

  const handleSimpleSearch = async (fromPosition = fromPos) => {
    setSpellingCorrectionEnabled(false);
    setLastSearchedLetter(null);
    handleSearch({
      endpoint: SEARCH_ENDPOINT,
      body: JSON.stringify(buildSearchPayload(input, fromPosition, true)),
      searchQuery: input
    });
  };

  // Wrapper functions for new searches that reset fromPos
  const handleNewAugmentSearch = async () => {
    setFromPos(0);
    await handleSearchWithCorrection(0);
    await handleIntentSearch(input);
  };

  const handleNewBasicSearch = async () => {
    setFromPos(0);
    await handleSimpleSearch(0);
    await handleIntentSearch(input);
  };

  // Pagination handlers
  const handlePreviousPage = async () => {
    const newFromPos = Math.max(0, fromPos - maxResults);
    setFromPos(newFromPos);
    if (lastSearchedLetter) {
      await runPrefixSearch(lastSearchedLetter, newFromPos);
    } else if (!spellingCorrectionEnabled) {
      // Use the last search type (augment is default)
      await handleSimpleSearch(newFromPos);
    } else {
      await handleSearchWithCorrection(newFromPos);
    }
  };

  const handleNextPage = async () => {
    const newFromPos = fromPos + maxResults;
    setFromPos(newFromPos);
    if (lastSearchedLetter) {
      await runPrefixSearch(lastSearchedLetter, newFromPos);
    } else if (!spellingCorrectionEnabled) {
      // Use the last search type (augment is default)
      await handleSimpleSearch(newFromPos);
    } else {
      await handleSearchWithCorrection(newFromPos);
    }
  };

  const handleSearch = async ({ endpoint, body, searchQuery, sortDocuments }) => {
    if (!endpoint) {
      console.error("Kein Endpoint definiert – Abbruch.");
      setError("Konfiguration fehlerhaft. Kein Endpoint definiert.");
      return;
    }

    setError(null);
    try {
      setIsLoading(true);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, endpoint: ${endpoint}, response: ${errorText}`);
      }

      const searchResults = await response.json();
      const externalIds = searchResults.results.map(item => item.external_id);

      const semanticSearchTerm = searchResults.search_terms?.find(
        (term) => term.target === 'SEMANTIC'
      );
      const correctedQuery = semanticSearchTerm?.text || searchQuery;
      const rawOriginalQuery = semanticSearchTerm?.original_text || searchQuery;

      if (externalIds.length === 0) {
        setData([]);
        setOriginalQuery(rawOriginalQuery);
        setQuery(correctedQuery);
        setIsLoading(false);
        return;
      }

      const documentsResponse = await fetch(DOCUMENTS_ENDPOINT, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify({ external_ids: externalIds }),
      });

      if (!documentsResponse.ok) {
        const errorText = await documentsResponse.text();
        throw new Error(`HTTP error! status: ${documentsResponse.status}, endpoint: ${DOCUMENTS_ENDPOINT}, response: ${errorText}`);
      }

      const documentsJson = await documentsResponse.json();
      let documents = documentsJson.documents.map((doc) => ({
        title: doc.title || '',
        broadcast_series_title: doc.broadcast_series_title || '',
        teaser_image_url: doc.teaser_image_url || '/stock-img.png',
        live_url: doc.live_url || null,
        teaser_text: doc.teaser_text || ''
      }));

      // DOCUMENTS_ENDPOINT doesn't necessarily preserve the order of the requested
      // external_ids (i.e. the search's sort_by), so callers that need a specific
      // client-side order (e.g. the A-Z letter search) can pass a sort function.
      if (sortDocuments) {
        documents = sortDocuments(documents);
      }

      setData(documents);
      setOriginalQuery(rawOriginalQuery);
      setQuery(correctedQuery);

      setIsLoading(false);
    } catch (err) {
      console.error('Failed to fetch error: ' + err.message);
      console.error("Failed to fetch: ", err);
      setError('Failed to fetch error: ' + err.message);
      setIsLoading(false);
    }
  };

  const handleAutocompleteSelect = async (suggestion) => {
    setSearchSuggestions([]);
    setInput(suggestion);
    setQuery(suggestion);
    await handleNewAugmentSearchForQuery(suggestion);
  };

  const handleNewAugmentSearchForQuery = async (nextQuery) => {
    setFromPos(0);
    setInput(nextQuery);
    setLastSearchedLetter(null);

    await handleSearch({
      endpoint: SEARCH_ENDPOINT,
      body: JSON.stringify(buildSearchPayload(nextQuery, 0)),
      searchQuery: nextQuery
    });

    await handleIntentSearch(nextQuery);
  };

  // A-Z letter search against PREFIX_SEARCH_ENDPOINT (/v3/fulltext-search), triggered from
  // the alphabet buttons in PlaceholderSearch. Like the regular search, this endpoint only
  // returns external_ids (+ sophora_id/score) - no metadata - so it's resolved via the same
  // handleSearch -> DOCUMENTS_ENDPOINT flow used for the hybrid/simple text search.
  const sortDocumentsAlphabetically = (documents) =>
    [...documents].sort((a, b) => a.title.localeCompare(b.title, 'de', { sensitivity: 'base' }));

  const runPrefixSearch = async (letter, fromPosition = 0) => {
    await handleSearch({
      endpoint: PREFIX_SEARCH_ENDPOINT,
      body: JSON.stringify(buildPrefixSearchPayload(letter, fromPosition)),
      searchQuery: letter,
      sortDocuments: sortDocumentsAlphabetically
    });
  };

  const handlePrefixSearch = async (letter) => {
    setFromPos(0);
    setInput(letter);
    setLastSearchedLetter(letter);
    setSpellingCorrectionEnabled(false); // no "Meintest Du doch...?" hint for a plain letter
    setIntentSuggestions([]);
    await runPrefixSearch(letter, 0);
  };

  // api/search/intent/invoke is not ready on the backend yet - disabled so it
  // doesn't constantly error. Re-enable this block once it's live:
  const handleIntentSearch = async (query) => {
    setIntentSuggestions([]);
    // if (!query) {
    //   setIntentSuggestions([])
    //   return;
    // }
    //
    //
    // try {
    //   const response = await fetch('api/search/intent/invoke', {
    //     method: 'POST',
    //     headers: createJsonPostHeaders(config),
    //     body: JSON.stringify({
    //       input: {
    //         query: query,
    //       },
    //     }),
    //   });
    //
    //   if (!response.ok) {
    //     throw new Error('Intent search failed');
    //   }
    //
    //   const json = await response.json();
    //   setIntentSuggestions(json);
    // } catch (err) {
    //   console.error('Failed to fetch error: ' + err.message);
    //   console.error("Failed to fetch: ", err);
    // }
  };

  if (isAuthLoading(auth)) {
    return <div>Loading...</div>;
  }

  return (
    <div className="App">
      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h2>Please Log In</h2>
            <button onClick={handleLogin}>Log In</button>
          </div>
        </div>
      )}
      {!isModalOpen && (
        <>
          <Sidebar
            isOpen={isSidebarOpen}
            sliderValue={sliderValue}
            onSliderChange={handleSliderChange}
            maxResults={maxResults}
            onMaxResultsChange={handleMaxResultsChange}
            selectedFields={selectedFields}
            setSelectedFields={setSelectedFields}
            fieldBoosts={fieldBoosts}
            setFieldBoosts={setFieldBoosts}
            docTypeBoosts={docTypeBoosts}
            setDocTypeBoosts={setDocTypeBoosts}
            filters={filters}
            setFilters={setFilters}
            notFilters={notFilters}
            setNotFilters={setNotFilters}
            localWeight={localWeight}
          />

          <button
            className="toggle-btn"
            onClick={toggleSidebar}
            aria-label="Sidebar umschalten"
          >
            {isSidebarOpen ? '←' : '→'}
          </button>

          <header className="App-header">
            <SearchField
              input={input}
              onInputChange={setInput}
              onSendClick={handleNewAugmentSearch}
              suggestions={searchSuggestions}
              suggestionsLoading={suggestionsLoading}
              onSuggestionSelect={handleAutocompleteSelect}
            />

            {
              isLoading ?
                <div className="spinner"></div>
                : error ?
                  <span className='App-content'>
                    <div className='error-msg'></div>
                    {error.includes('400') ? 'Bitte formuliere deine Suchanfrage um' : 'Oops - etwas ist schief gegangen'}
                    <div className="reset-btn" onClick={handleReset}>reset</div>
                  </span>
                  : <div className='App-content'>
                    {spellingCorrectionEnabled && query && <div onClick={handleNewBasicSearch} className="basic-search-query">Meintest Du doch: {originalQuery}? </div>}
                    {query ?
                      <>
                        <ResultCardList
                          title={query}
                          items={data}
                          originalQuery={originalQuery}
                          intentSuggestions={intentSuggestions}
                          onSuggestionClick={async (suggestion) => {
                            setIsLoading(true);
                            setInput(suggestion);
                            setQuery(suggestion);
                            setFromPos(0);
                            setLastSearchedLetter(null);
                            await handleIntentSearch(suggestion);
                            await handleSearch({
                              endpoint: SEARCH_ENDPOINT,
                              body: JSON.stringify(buildSearchPayload(suggestion, 0)),
                              searchQuery: suggestion
                            });
                          }}
                        />
                        <Pagination
                          fromPos={fromPos}
                          maxResults={maxResults}
                          onPrevious={handlePreviousPage}
                          onNext={handleNextPage}
                          hasResults={data.length}
                        />
                      </>
                      : <PlaceholderSearch onLetterClick={handlePrefixSearch} />
                    }
                    {query ? (<div className="reset-btn" onClick={handleReset}>reset</div>) : null}
                  </div>
            }
          </header>
          <Footer />
        </>
      )}
    </div>
  );
}

export default App;
